import type { InputSnapshot } from '../../input/actions';
import type { Bounds } from '../bullets/BulletPool';
import { clamp } from '../../core/math';
import { SIM_HZ } from '../../core/GameLoop';

const IFRAME_SECONDS = 1.2;

export class Player {
  x = 0;
  y = 0;
  prevX = 0;
  prevY = 0;
  /** Visual radius; the hitbox is the small core (bullet-hell convention). */
  radius = 14;
  hitRadius = 4;
  speed = 260;
  maxHp = 6;
  hp = 6;
  /** In sim steps. */
  iframes = 0;
  /** Curse (seconds): healing is halved while active. */
  curseLeft = 0;
  /**
   * Aspect heal gating: 'all' (Vane — descent is her only heal; raising
   * maxHp still credits its delta, an accepted stat-plumbing wrinkle),
   * 'exceptLeech' (Thirl — lifesteal is his only recovery).
   */
  healBlock: 'none' | 'all' | 'exceptLeech' = 'none';
  alive = true;

  /** Set when a floor's TileMap exists; resolves wall sliding. */
  moveResolver: ((px: number, py: number, nx: number, ny: number, r: number) => [number, number]) | null = null;

  place(x: number, y: number): void {
    this.x = this.prevX = x;
    this.y = this.prevY = y;
  }

  update(dt: number, input: InputSnapshot, bounds: Bounds): void {
    this.prevX = this.x;
    this.prevY = this.y;
    if (this.iframes > 0) this.iframes--;
    if (this.curseLeft > 0) this.curseLeft -= dt;

    const nx = this.x + input.moveX * this.speed * dt;
    const ny = this.y + input.moveY * this.speed * dt;
    if (this.moveResolver) {
      [this.x, this.y] = this.moveResolver(this.x, this.y, nx, ny, this.radius * 0.7);
    } else {
      this.x = nx;
      this.y = ny;
    }
    this.x = clamp(this.x, bounds.minX, bounds.maxX);
    this.y = clamp(this.y, bounds.minY, bounds.maxY);
  }

  /** Heal, halved while cursed; aspect blocks may refuse it entirely. */
  heal(amount: number, source: 'generic' | 'leech' = 'generic'): number {
    if (this.healBlock === 'all') return 0;
    if (this.healBlock === 'exceptLeech' && source !== 'leech') return 0;
    const effective = this.curseLeft > 0 ? amount * 0.5 : amount;
    const before = this.hp;
    this.hp = Math.min(this.maxHp, this.hp + effective);
    return this.hp - before;
  }

  /** Returns true if damage landed (not invulnerable). */
  hit(damage: number): boolean {
    if (this.iframes > 0 || !this.alive) return false;
    this.hp -= damage;
    this.iframes = Math.round(IFRAME_SECONDS * SIM_HZ);
    if (this.hp <= 0) {
      this.hp = 0;
      this.alive = false;
    }
    return true;
  }
}
